'use client';

import { Button, Divider, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import {
  emailValidation,
  isValidEmail,
} from '@/components/form/validation/emailValidation';
import { Link } from '@/components/shared/link';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { getApiErrorMessage } from '@/utils/apiError';

import { inputSx } from './fieldStyles';
import { LoginCard } from './loginCard';
import { NewPasswordFields } from './newPasswordFields';
import { VerificationCodeField } from './verificationCodeField';

type Phase = { name: 'request' } | { name: 'confirm' };

export const ForgotPasswordV2Page = () => {
  const { nonProjectDal: dal } = useNonProjectDal();
  const searchParams = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phase, setPhase] = useState<Phase>({ name: 'request' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const passwordsMatch = newPassword === confirmPassword;

  const requestCode = async () => {
    if (!isValidEmail(email)) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // The response carries no destination on purpose (the backend returns an
      // identical empty body for existing and non-existing users to prevent
      // enumeration), so the confirm step addresses the email the user typed.
      await dal.authV2.forgotPassword({ email });
      setPhase({ name: 'confirm' });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Failed to send reset code'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmReset = async () => {
    if (!code || !newPassword || !passwordsMatch) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await dal.authV2.confirmForgotPassword({
        email,
        confirmationCode: code,
        newPassword,
      });
      setDone(true);
    } catch (e) {
      // Server errors always surface in the banner below the form; only the
      // client-side password-mismatch check renders inline on the field.
      setError(getApiErrorMessage(e, 'Failed to reset password'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginCard
      width='464px'
      error={error}
      errorTestId='forgot-password-v2-error'
    >
      {done && (
        <Typography variant='subtitle1' className='text-center'>
          Your password has been reset. You can now sign in.
        </Typography>
      )}

      {!done && phase.name === 'request' && (
        <>
          <Typography variant='subtitle1' className='text-center'>
            Enter your email and we&apos;ll send you a password reset code.
          </Typography>
          <Stack
            component='form'
            onSubmit={(event) => {
              event.preventDefault();
              void requestCode();
            }}
            direction='row'
            sx={{ gap: '16px', alignItems: 'center', width: '100%' }}
          >
            <FormTextField
              autoFocus
              title=''
              placeholder='name@company.com'
              dataTestId='forgot-password-v2-email'
              validation={emailValidation}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ flex: 1, ...inputSx }}
            />
            <Button
              type='submit'
              variant='contained'
              disabled={!isValidEmail(email) || submitting}
              data-testid='forgot-password-v2-submit'
              sx={{ height: '48px' }}
            >
              {submitting ? 'Sending…' : 'Send Reset Code'}
            </Button>
          </Stack>
        </>
      )}

      {!done && phase.name === 'confirm' && (
        <>
          <Typography variant='subtitle1' className='text-center'>
            {`We sent a code to ${email}. Enter it below along with a new password. If you don't see the email in your inbox, check your spam folder as well.`}
          </Typography>
          <Stack
            component='form'
            onSubmit={(event) => {
              event.preventDefault();
              void confirmReset();
            }}
            sx={{ width: '100%', gap: '12px' }}
          >
            <VerificationCodeField
              id='forgot-password-v2-code'
              value={code}
              onChange={setCode}
            />
            <NewPasswordFields
              idPrefix='forgot-password-v2'
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              onNewPasswordChange={setNewPassword}
              onConfirmPasswordChange={setConfirmPassword}
            />
            <Button
              type='submit'
              fullWidth
              variant='contained'
              disabled={!code || !newPassword || !passwordsMatch || submitting}
              data-testid='forgot-password-v2-confirm'
              sx={{ height: '48px' }}
            >
              {submitting ? 'Resetting…' : 'Reset password'}
            </Button>
          </Stack>
        </>
      )}

      <Divider
        sx={{
          width: '100%',
          borderStyle: 'dashed',
          borderColor: 'var(--mui-palette-divider)',
        }}
      />
      <Link href='/login' className='font-bold no-underline'>
        Back to Login
      </Link>
    </LoginCard>
  );
};
